/**
 * Pure status → board-column mapping for the FP1 Orders board.
 *
 * This is a READ-ONLY presentation helper. It does NOT change how order status
 * is set or transitioned on the backend — it only decides which of the three
 * kanban columns an order card should render in.
 *
 * The canonical status vocabulary comes from the backend
 * (`GET admin/marketplace/orders/order-statuses`). The known real values that
 * appear in this codebase's `GetOrdersParams.order_status` union are:
 *
 *   received, processing, shipping, completed, cancelled, backfilled, archived
 *
 * We additionally accept a handful of common synonyms so the board keeps
 * working if the backend enum grows (e.g. `shipped`, `in_transit`, `delivered`,
 * `pending`) without a frontend change. Anything we don't recognise at all is
 * still shown — surfaced in the Processing column and visually flagged as
 * `unrecognized` so it can never be silently miscategorised.
 */

export type OrderColumnKey = 'processing' | 'in_transit' | 'completed';

export interface StatusClassification {
    /** Which of the three board columns this status renders in. */
    column: OrderColumnKey;
    /**
     * True when the status matched none of the known status groups. Such cards
     * are shown in the Processing column but flagged in the UI so a reviewer
     * knows the bucketing is a best-effort guess, not a confident match.
     */
    unrecognized: boolean;
    /**
     * True for terminal/inactive statuses (cancelled, backfilled, archived).
     * These are no longer "in flight", so they are never treated as "stuck",
     * and they render in the Completed column with muted styling.
     */
    inactive: boolean;
}

/** Statuses meaning "order placed / being prepared, not yet shipped". */
const PROCESSING_STATUSES = new Set([
    'received',
    'processing',
    'pending',
    'preparing',
    'created',
    'new',
]);

/** Statuses meaning "left the warehouse, not yet marked done". */
const IN_TRANSIT_STATUSES = new Set([
    'shipping',
    'shipped',
    'in_transit',
    'transit',
    'out_for_delivery',
    'dispatched',
]);

/** Terminal "done / fulfilled" statuses. */
const COMPLETED_STATUSES = new Set(['completed', 'complete', 'delivered', 'fulfilled']);

/** Recognised-but-inactive statuses (terminal, but not a successful completion). */
const INACTIVE_STATUSES = new Set([
    'cancelled',
    'canceled',
    'backfilled',
    'archived',
    'refunded',
    'returned',
]);

/**
 * Classify a raw order status string into a board column.
 *
 * @param rawStatus the order's `order_status` value (any casing / spacing).
 */
export const classifyOrderStatus = (rawStatus: string | null | undefined): StatusClassification => {
    const status = (rawStatus ?? '').trim().toLowerCase();

    if (PROCESSING_STATUSES.has(status)) {
        return { column: 'processing', unrecognized: false, inactive: false };
    }
    if (IN_TRANSIT_STATUSES.has(status)) {
        return { column: 'in_transit', unrecognized: false, inactive: false };
    }
    if (COMPLETED_STATUSES.has(status)) {
        return { column: 'completed', unrecognized: false, inactive: false };
    }
    if (INACTIVE_STATUSES.has(status)) {
        // Terminal but not a successful completion — park it in the Completed
        // column (out of the active flow) and mark it inactive so it is never
        // flagged as stuck.
        return { column: 'completed', unrecognized: false, inactive: true };
    }

    // Unknown status: surface it prominently (leftmost column) and flag it so a
    // human notices rather than trusting a silent guess.
    return { column: 'processing', unrecognized: true, inactive: false };
};

/** Convenience: the column an order status maps to. */
export const columnForStatus = (rawStatus: string | null | undefined): OrderColumnKey =>
    classifyOrderStatus(rawStatus).column;
